<template>
  <section v-if="activeSubsection" class="row">
    <card>
      <div class="d-flex flex-wrap">
        <div class="col-12 mb-4">
          <div class="row">
            <div class="col-12">
              <h3>Editing {{ activeSubsection.label }}</h3>
              <button class="btn btn-sm btn-primary" @click="viewAll">View all releases</button>
              <button class="btn btn-sm btn-primary ml-1" @click="saveSubsection(activeSubsection)">Save</button>
            </div>
          </div>
        </div>
        <div class="col-6 mb-4">
          <div class="row">
            <div class="col-6 mb-4">
              Label
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeSubsection.label"
                placeholder="i.e. apartment"
              />
            </div>
            <div class="col-6 mb-4">
              Display Name
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeSubsection.displayName"
                placeholder="i.e. apartment"
              />
            </div>
            <div class="col-6 mb-4">
              Is Tag and Slide Hidden <br>
              <toggle-button
                v-model="activeSubsection.isVisuallyHidden" 
                color="#28a745" 
                :sync="true" 
                :labels="false" 
                :width="50" 
                :height="30" 
                style="margin: 3px 0 0;"
              />
            </div>
            <div class="col-6 mb-4">
              Is Filter Slideout Hidden <br>
              <toggle-button
                v-model="activeSubsection.isFilterHidden" 
                color="#28a745" 
                :sync="true" 
                :labels="false" 
                :width="50" 
                :height="30" 
                style="margin: 3px 0 0;"
              />
            </div>

            <div class="col-6 mb-4">
              Subsection Type
              <select v-model="activeSubsection.type" class="form-control">
                <option value="">Select type</option>
                <option value="apartments">Apartments</option>
                <option value="townhouses">Townhouses</option>
                <option value="houses">Houses</option>
                <option value="land">Land</option>
              </select>
            </div>

            <div class="col-12 mb-4">
              Description
              <ck-editor v-model="activeSubsection.description" id="subsectionDescription"></ck-editor>
            </div>

            <div class="col-12 mb-4">
              Issuu Embed Url
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeSubsection.issuuEmbedUrl"
                placeholder=""
              />
            </div>

            <div class="col-12 mb-4">
              Subsection Background
              <input-asset v-model="activeSubsection.backgroundImage"></input-asset>
            </div>

            <div class="col-12 mb-4">
              Card Background Image
              <input-asset v-model="activeSubsection.cardBackgroundImage"></input-asset>
            </div>

            <div class="col-12 mb-4">
              Subsection Minimap
              <input-asset v-model="activeSubsection.minimapImage"></input-asset>
            </div>
          </div>
        </div>

        <div class="col-6 mb-4">
          <div class="row">
            <div class="col-12 mb-4">
              Associated Properties
              <input-multi-select
                v-model="activeSubsection.propertyIds"
                :options="propertyOptions"
              ></input-multi-select>
            </div>

            <div class="col-12 mb-4">
              Associated Subsections*
              <p><small>*Only populate for subsections that require subsections as children. i.e Building > Floor</small></p>
              <input-multi-select
                v-model="activeSubsection.subsectionIds"
                :options="subsectionOptions"
              ></input-multi-select>     
            </div>

            <div class="col-12 mb-4">
              Subsection Type*
              <p><small>*Only populate for subsections that require subsections as children. i.e Building > Floor</small></p>
              <input
                type="text"
                class="form-control form-control-lg"
                v-model="activeSubsection.subsectionType"
                placeholder="i.e. block, floor, etc."
              />        
            </div>
          </div>
        </div>

        <div class="col-12 mb-4">
          <label>Tags</label>
          <button class="btn btn-sm btn-primary my-2" @click="addSubsectionTag">add tag</button>
          <p>
            <info-box>
              <ul>
                <li>Click on the Add Tag button to create a tag</li>
                <li>Reposition tags by clicking and dragging</li>
                <li>Click on a tag to display the edit tag dialog box</li>
              </ul>
            </info-box>
          </p>
          <draggable-elements 
            :tags="activeSubsection.tags"
            :property-ids="activeSubsection.propertyIds"
            :subsection-ids="activeSubsection.subsectionIds"
            :background-image="activeSubsection.backgroundImage"
          />
        </div>
      </div>
    </card>
  </section>
</template>
<script>
import { includes, find } from 'lodash'
import { mapGetters } from 'vuex'
import CkEditor from '@/components/CKEditor'
import DotDropper from '@/components/DotDropper'
import InputAsset from '@/components/InputAsset'
import InputMultiSelect from '@/components/InputMultiSelect'
import LivingOptionTags from '@/components/LivingOptionTags'
import LivingOptionMarkers from '@/components/LivingOptionMarkers'
import InfoBox from '@/components/InfoBox'
import DraggableElements from '@/components/DraggableElements'

export default {
  components: { 
    CkEditor,
    DotDropper,
    InputAsset,
    InputMultiSelect,
    LivingOptionTags,
    LivingOptionMarkers,
    InfoBox,
    DraggableElements
  },
  
  data () {
    return {
      activeSubsectionId: null,
      initialPropertyIds: [],
      subsectionFilterText: '',
      filterActiveStates: []
    }
  },

  computed: {
    ...mapGetters([
      'subsections',
      'properties',
    ]),

    activeSubsection () {
      return this.subsections.find(subsection => subsection._id === this.activeSubsectionId)
    },  

    subsectionOptions () {
      let subsectionOptions = []
      this.subsections.forEach(subsection => {
        const isNotUsedLivingOption = subsection.parent.type !== 'livingOption' // is not being used by a living option
        const isNotUsedSubsection = subsection.parent.id === null || subsection.parent.id === this.activeSubsectionId // is not being used by a different subsection
        const isNotSelf = subsection._id !== this.activeSubsectionId // is not itself

        if (isNotUsedLivingOption && isNotUsedSubsection && isNotSelf) {
          subsectionOptions.push({
            label: subsection.label,
            value: subsection._id
          })
        }
      })
      return subsectionOptions
    },

    propertyOptions () {
      let propertyOptions = []
      this.properties.forEach(property => {
        const isNotUsedSubsection = property.parent.id === null || property.parent.id === this.activeSubsectionId // is not being used by a different subsection

        if (isNotUsedSubsection) {
          propertyOptions.push({
            label: property.label,
            value: property._id
          })
        }
      })
      return propertyOptions
    },
  },

  watch: {
    activeSubsection (subsection) {
      this.initialPropertyIds = subsection ? subsection.propertyIds : []
    }
  },

  created () {
    this.$store.dispatch('FETCH_ASSETS')
    this.$store.dispatch('FETCH_SUBSECTIONS')
    this.$store.dispatch('FETCH_PROPERTIES')
  },

  mounted () {
    this.activeSubsectionId = this.$route.params.id
  },

  methods: {
    viewAll () {
      this.$router.push('/live/subsections')
    },

    saveSubsection (subsection) {
      this.$store.dispatch('SAVE_SUBSECTION', subsection)

      // reset parent property on removed properties
      const matchedProperties = this.properties.filter(property => property.parent.id === this.activeSubsectionId)
      const removedPropertiesIds = matchedProperties.filter(property => {
        if (!includes(this.activeSubsection.propertyIds, property._id)) {
          return true
        }
      }).map(property => property._id)

      removedPropertiesIds.forEach(propertyId => {
        const property = find(this.properties, property => propertyId === property._id)
        const propertyUpdate = {
          _id: property._id, 
          marker: property.marker,
          polygon: property.polygon,
          parent: {
            id: null,
            type: ''
          }
        }
      
        this.$store.dispatch('SAVE_PROPERTY', propertyUpdate)
      })


      subsection.propertyIds.forEach(propertyId => {
        const property = find(this.properties, property => propertyId === property._id)
        if (property) {
          const propertyUpdate = {
            _id: property._id,
            marker: property.marker,
            polygon: property.polygon,
            parent: {
              id: this.activeSubsectionId,
              type: 'subsection'
            }
          }
          this.$store.dispatch('SAVE_PROPERTY', propertyUpdate)
        }
      })

      // reset parent property on removed subsections
      const matchedSubsections = this.subsections.filter(subsection => subsection.parent.id === this.activeSubsectionId)
      const removedSubsectionsIds = matchedSubsections.filter(subsection => {
        if (!includes(this.activeSubsection.subsectionIds, subsection._id)) {
          return true
        }
      }).map(subsection => subsection._id)

      removedSubsectionsIds.forEach(subsectionId => {
        const subsection = find(this.subsections, subsection => subsectionId === subsection._id)
        const subsectionUpdate = {
          _id: subsection._id, 
          marker: subsection.marker,
          parent: {
            id: null,
            type: ''
          }
        }
      
        this.$store.dispatch('SAVE_SUBSECTION', subsectionUpdate)
      })

      subsection.subsectionIds.forEach(subsectionId => {
        const subsection = find(this.subsections, subsection => subsectionId === subsection._id)
        const subsectionUpdate = {
          _id: subsection._id, 
          marker: subsection.marker,
          polygon: subsection.polygon,
          parent: {
            id: this.activeSubsectionId,
            type: 'subsection'
          }
        }

        this.$store.dispatch('SAVE_SUBSECTION', subsectionUpdate)
      })
      
      this.viewAll()
    },

    addSubsectionTag () {
      this.$store.dispatch('GET_TAG_SCHEMA').then(res => {
        this.activeSubsection.tags.push(res.data)
      })
    }
  },
}
</script>
